import type { WizardStepProps } from '../../../../types/builder.types'
import { getAllFields } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { TableFieldsModal } from '../TableFieldsModal'
import { useMemo, useState } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function AreaChartConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()
  const [showTableFields, setShowTableFields] = useState(false)

  const actualDataSource = config.dataSource === "custom_query" 
  ? config.customQuery?.config?.baseTable
  : config.dataSource;

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  const { allFields } = useMemo(() => {
    if (!config.dataSource) return { allFields: [] as ReturnType<typeof fieldsFromCustomQuery> }

    if (
      (config.dataSource === 'custom_query' ||
        (config.dataSource?.includes('ckan_data_generic') && config.customQuery)) &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      const fieldsArray = fieldsFromCustomQuery(config.customQuery)
      return { allFields: fieldsArray }
    }

    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return {
        allFields: dynamicSource.fields,
      }
    }

    return {
      allFields: getAllFields(config.dataSource),
    }
  }, [dataSources, config.dataSource, cqSig])

  const areaConfig = config.areaChartConfig || { xAxisKey: '', yAxisKeys: [], stacked: true }
  const isValid = Boolean(
    config.metadata?.title && 
    areaConfig.xAxisKey && 
    areaConfig.yAxisKeys?.length > 0
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>
      
      <h2 className="text-2xl font-bold mb-2">Configurar Gráfico de Área</h2>
      <p className="text-gray-600 mb-6">
        Evolución temporal con múltiples series
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">

        <div>
                  {/* Botón o trigger para mostrar los campos */}
                  <button
                    onClick={() => setShowTableFields(true)}
                    disabled={!config.dataSource}
                    className="px-6 py-2 text-sm rounded-lg whitespace-nowrap transition-all border bg-blue-600 text-white border-blue-600 shadow-sm"
                  >
                    Ver campos disponibles para fuente de datos : <b>{actualDataSource}</b>
                  </button>
        
                  {/* Modal de campos (incluye fuentes BMP) */}
                   {showTableFields && actualDataSource && (
                      <TableFieldsModal
                        dataSource={actualDataSource}
                        componentType={config.componentType}
                        dataSources={dataSources}
                        onClose={() => setShowTableFields(false)}
                      />
                    )}
                </div>
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Evolución de Carbono y Biomasa"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) => 
              onChange({ 
                ...config, 
                metadata: { ...config.metadata, title: e.target.value } 
              })
            }
          />
        </div>

        {/* Eje X */}
        <div>
          <label className="block font-medium mb-2">
            Eje X (Categoría) <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={areaConfig.xAxisKey || ''}
            onChange={(e) => 
              onChange({ 
                ...config, 
                areaChartConfig: { ...areaConfig, xAxisKey: e.target.value } 
              })
            }
          >
            <option value="">Selecciona una categoría...</option>
            {allFields.map(field => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Categoría para el eje horizontal (ej: municipio, año)
          </p>
        </div>

        {/* Eje Y (múltiple) */}
        <div>
          <label className="block font-medium mb-2">
            Series del Eje Y <span className="text-red-500">*</span>
          </label>
          <div className="border rounded p-3 bg-gray-50">
            <div className="space-y-2">
              {allFields
                .filter(field => field.type === 'number' || field.aggregatable)
                .map(field => (
                  <label key={field.name} className="flex items-center gap-3 hover:bg-gray-100 p-2 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={areaConfig.yAxisKeys?.includes(field.name) || false}
                      onChange={(e) => {
                        const currentKeys = areaConfig.yAxisKeys || [];
                        const newKeys = e.target.checked
                          ? [...currentKeys, field.name]
                          : currentKeys.filter(key => key !== field.name);
                        
                        onChange({
                          ...config,
                          areaChartConfig: { ...areaConfig, yAxisKeys: newKeys }
                        });
                      }}
                      className="w-5 h-5"
                    />
                    <span className="font-medium">{field.label}</span>
                  </label>
                ))
              }
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Seleccionados: {areaConfig.yAxisKeys?.join(', ') || 'ninguno'}
          </p>
        </div>

        {/* Apilado */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="stacked"
            checked={areaConfig.stacked !== false}
            onChange={(e) => 
              onChange({ 
                ...config, 
                areaChartConfig: { ...areaConfig, stacked: e.target.checked } 
              })
            }
            className="w-5 h-5"
          />
          <label htmlFor="stacked" className="font-medium cursor-pointer">
            Series apiladas
          </label>
        </div>

        {/* Mostrar grid */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="showGrid"
            checked={areaConfig.showGrid !== false}
            onChange={(e) => 
              onChange({ 
                ...config, 
                areaChartConfig: { ...areaConfig, showGrid: e.target.checked } 
              })
            }
            className="w-5 h-5"
          />
          <label htmlFor="showGrid" className="font-medium cursor-pointer">
            Mostrar cuadrícula
          </label>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}